import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Truck, ChevronRight, ScanBarcode } from 'lucide-react';
import { useInspectionStore } from '@/context/inspectionStore';
import { useCurrentUser } from '@/components/auth/AuthProvider';
import { useRole } from '@/hooks/useRole';
import { useAssignedVehicles } from '@/hooks/useAssignedVehicles';
import type { Vehicle, SamsaraVehicle } from '@/types';
import { QRScanner } from '@/components/common/QRScanner';
import { VehicleConfirmModal } from '@/components/common/VehicleConfirmationModal';
import { useQRVehicleScan } from '@/hooks/useQRVehicleScan';
import { getAllSchedules, getOverdueStatus, formatDueDate, type VehicleSchedule } from '@/services/scheduleService';
import { InspectionDueBadge } from '@/components/common/InspectionDueBadge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

export function VehicleSelectPage() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { isAdmin, loading: roleLoading } = useRole();
  const startInspection = useInspectionStore((s) => s.startInspection);

  const { vehicles, loading: vehiclesLoading, error, noVehicleReason } = useAssignedVehicles();
  const [search, setSearch] = React.useState('');
  const [schedules, setSchedules] = React.useState<Record<string, VehicleSchedule>>({});
  const [schedulesLoading, setSchedulesLoading] = React.useState(true);

  const {
    scannerOpen, scannedVehicle, looking,
    openScanner, handleScan, closeScanner, closeConfirm,
  } = useQRVehicleScan();

  React.useEffect(() => {
    getAllSchedules()
      .then(data => {
        const map: Record<string, VehicleSchedule> = {};
        data.forEach(s => { map[s.vehicleId] = s; });
        setSchedules(map);
      })
      .catch(console.error)
      .finally(() => setSchedulesLoading(false));
  }, []);

  const loading = roleLoading || vehiclesLoading || schedulesLoading;

  const filtered = vehicles.filter(
    (v) =>
      v.name?.toLowerCase().includes(search.toLowerCase()) ||
      v.vin?.toLowerCase().includes(search.toLowerCase()) ||
      `${v.year ?? ''} ${v.make ?? ''} ${v.model ?? ''}`.toLowerCase().includes(search.toLowerCase())
  );

  function handleSelect(sv: SamsaraVehicle) {
    if (!user) return;

    // If an inspection for this vehicle is already in progress, skip startInspection
    // to avoid wiping existing photos and check results
    const existingInspection = useInspectionStore.getState().activeInspection;
    if (existingInspection && existingInspection.vehicleId === sv.id && existingInspection.status === 'in-progress') {
      navigate('/inspect/type');
      return;
    }

    const vehicle: Vehicle = {
      id: sv.id,
      unitNumber: sv.name,
      vin: sv.vin,
      year: sv.year,
      make: sv.make,
      model: sv.model,
      licensePlate: sv.licensePlate || '',
      state: '',
      mileage: sv.odometerMeters ? Math.round(sv.odometerMeters / 1609.34) : 0,
      samsaraId: sv.id,
      status: 'active',
    };

    const expectedVehicleDescription = `${sv.year} ${sv.make} ${sv.model} — unit ${sv.name}`;

    startInspection(
      vehicle,
      'daily',
      { id: user.id, name: user.name, email: user.email, role: user.role },
      expectedVehicleDescription,
    );

    navigate('/inspect/type');
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6 dark:bg-gray-800 dark:text-gray-100 min-h-full">
      <h1 className="text-2xl font-bold text-wire3-navy dark:text-wire3-green">New Inspection</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {isAdmin
          ? 'Select a vehicle to inspect.'
          : 'Your assigned vehicle is shown below. Scan a VIN to use a different vehicle.'}
      </p>

      {/* Search + VIN scanner */}
      <div className="relative mt-5">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder={isAdmin ? 'Search by unit #, VIN, or make/model...' : 'Search your vehicle...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-10 pr-12"
        />
        <button
          onClick={openScanner}
          title="Scan VIN barcode to select a vehicle"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg bg-wire3-navy/5 dark:bg-wire3-green/10 p-2 text-wire3-navy dark:text-wire3-green transition-colors hover:bg-wire3-navy/10 dark:hover:bg-wire3-green/20"
        >
          <ScanBarcode className="h-5 w-5" />
        </button>
      </div>

      {/* Vehicle list */}
      <div className="mt-4 flex flex-col gap-3">
        {loading && <LoadingSpinner message="Loading vehicles..." />}

        {!loading && error && (
          <div className="rounded-lg border border-status-fail/20 bg-status-fail/5 p-4">
            <p className="text-sm font-semibold text-status-fail">Failed to load vehicles</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{error}</p>
          </div>
        )}

        {!loading && !isAdmin && vehicles.length === 0 && !error && (
          <div className="mt-2 rounded-lg border border-status-warning/30 bg-status-warning/5 p-4">
            <p className="text-sm font-semibold text-status-warning">No assigned vehicle found</p>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              You don't have a vehicle assigned in Samsara. Scan the VIN barcode on a vehicle to proceed.
            </p>
            {noVehicleReason && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">{noVehicleReason}</p>
            )}
          </div>
        )}

        {!loading && filtered.length === 0 && vehicles.length > 0 && (
          <div className="flex flex-col items-center py-12 text-gray-400">
            <Truck className="mb-2 h-10 w-10" />
            <p>No vehicles match your search</p>
          </div>
        )}

        {!loading && filtered.map((v) => {
          const schedule = schedules[v.id];
          const dailyStatus = getOverdueStatus(schedule?.nextDailyDue ?? new Date().toISOString());
          const weeklyStatus = getOverdueStatus(schedule?.nextWeeklyDue ?? new Date().toISOString());
          const monthlyStatus = getOverdueStatus(schedule?.nextMonthlyDue ?? new Date().toISOString());

          return (
            <button
              key={v.id}
              onClick={() => handleSelect(v)}
              className="card dark:bg-gray-750 dark:border-gray-700 flex flex-col gap-2 text-left transition-shadow hover:shadow-md dark:hover:bg-gray-700 active:scale-[0.99] w-full"
            >
              <div className="flex items-center gap-4 w-full">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-wire3-navy/5 dark:bg-wire3-green/10">
                  <Truck className="h-6 w-6 text-wire3-navy dark:text-wire3-green" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-wire3-navy dark:text-wire3-green">{v.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{v.year} {v.make} {v.model}</p>
                  <p className="truncate text-xs text-gray-400 dark:text-gray-500 font-mono">{v.vin}</p>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-gray-300 dark:text-gray-600" />
              </div>

              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100 dark:border-gray-700 w-full">
                <InspectionDueBadge
                  type="daily"
                  status={dailyStatus}
                  label={schedule?.nextDailyDue ? formatDueDate(schedule.nextDailyDue) : 'No record'}
                />
                <InspectionDueBadge
                  type="weekly"
                  status={weeklyStatus}
                  label={schedule?.nextWeeklyDue ? formatDueDate(schedule.nextWeeklyDue) : 'No record'}
                />
                <InspectionDueBadge
                  type="monthly"
                  status={monthlyStatus}
                  label={schedule?.nextMonthlyDue ? formatDueDate(schedule.nextMonthlyDue) : 'No record'}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Loading overlay while looking up scanned vehicle */}
      {looking && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="rounded-xl bg-white dark:bg-gray-800 px-6 py-4 shadow-xl flex items-center gap-3">
            <LoadingSpinner size="sm" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Looking up vehicle...</p>
          </div>
        </div>
      )}

      {scannerOpen && <QRScanner onScan={handleScan} onClose={closeScanner} />}

      {scannedVehicle && (
        <VehicleConfirmModal
          vehicle={scannedVehicle}
          onConfirm={() => { closeConfirm(); handleSelect(scannedVehicle); }}
          onClose={closeConfirm}
        />
      )}
    </div>
  );
}
